"use client";

import { Icon, SectionCard, StatusBadge } from "@repo/dashboard-core";
import { useTranslations } from "next-intl";

import type {
  CallerName,
  IdentityMatch,
  LineStatus,
  LineTypeIntelligence,
  PackageResult,
  ReassignedNumber,
  SimSwap,
  SmsPumpingRisk,
} from "@/lib/types/phone-checker";

type BadgeVariant = "success" | "warning" | "error" | "info" | "neutral";

/**
 * A refused package is not an error.
 *
 * Twilio answers a package the account is not approved for, or one that has no
 * data for this country, with an HTTP 200 and a per-package code. Painting all
 * of those red would tell a customer their number failed seven checks when it
 * passed four and the other three were never run. `neutral` is the honest
 * colour for "we did not get to ask".
 */
const STATUS_VARIANT: Record<string, BadgeVariant> = {
  not_requested: "neutral",
  missing_input: "info",
  invalid_input: "warning",
  not_enabled: "neutral",
  no_coverage: "neutral",
  rate_limited: "warning",
  wrong_region: "neutral",
  unavailable: "warning",
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-surface-border py-2 last:border-b-0">
      <span className="text-sm text-text-secondary">{label}</span>
      <span className="text-right text-sm font-medium text-text-primary">
        {value}
      </span>
    </div>
  );
}

export function PackageCard({ result }: { result: PackageResult }) {
  const t = useTranslations("phone-checker");

  const title = t(`package.${result.package}`);
  const description = t(`package-description.${result.package}`);

  if (result.status !== "ok") {
    return (
      <SectionCard title={title} description={description}>
        <div className="flex flex-col items-start gap-2">
          <StatusBadge
            label={t(`status.${result.status}`)}
            variant={STATUS_VARIANT[result.status] ?? "neutral"}
          />
          <p className="text-sm text-text-secondary">
            {t(`status-description.${result.status}`)}
          </p>
          {result.errorCode !== null && (
            <p className="text-xs text-text-secondary">
              {t("reference-code", { code: result.errorCode })}
            </p>
          )}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={title} description={description}>
      <PackageBody result={result} />
    </SectionCard>
  );
}

function PackageBody({ result }: { result: PackageResult }) {
  const t = useTranslations("phone-checker");
  const unknown = t("value-unknown");

  switch (result.package) {
    case "line_type_intelligence": {
      const data = result.data as LineTypeIntelligence;
      // Twilio returns no carrier for the non-carrier line types (tollFree,
      // premium, voicemail…), so an empty carrier is expected there rather
      // than a gap worth apologising for.
      return (
        <div>
          <Field
            label={t("field.line-type")}
            value={data.type ? t(`line-type.${data.type}`) : unknown}
          />
          <Field
            label={t("field.carrier")}
            value={data.carrierName ?? unknown}
          />
          {data.mobileCountryCode && (
            <Field
              label={t("field.network-code")}
              value={`${data.mobileCountryCode}${
                data.mobileNetworkCode ? ` / ${data.mobileNetworkCode}` : ""
              }`}
            />
          )}
        </div>
      );
    }

    case "line_status": {
      const data = result.data as LineStatus;
      const status = data.status ?? "unknown";
      const variant: BadgeVariant =
        status === "reachable" || status === "active"
          ? "success"
          : status === "inactive"
            ? "error"
            : status === "unreachable"
              ? "warning"
              : "neutral";

      return (
        <div className="flex flex-col items-start gap-2">
          <StatusBadge label={t(`line-status.${status}`)} variant={variant} />
          <p className="text-sm text-text-secondary">
            {t(`line-status-description.${status}`)}
          </p>
        </div>
      );
    }

    case "caller_name": {
      const data = result.data as CallerName;
      return (
        <div>
          <Field
            label={t("field.caller-name")}
            value={data.callerName ?? unknown}
          />
          <Field
            label={t("field.caller-type")}
            value={
              data.callerType ? t(`caller-type.${data.callerType}`) : unknown
            }
          />
        </div>
      );
    }

    case "sim_swap": {
      const data = result.data as SimSwap;
      // `swappedInPeriod` is only meaningful next to the window Twilio was
      // configured with, so the window is always shown beside it.
      return (
        <div className="flex flex-col items-start gap-2">
          <StatusBadge
            label={
              data.swappedInPeriod
                ? t("sim-swap.swapped")
                : data.swappedInPeriod === false
                  ? t("sim-swap.not-swapped")
                  : t("sim-swap.unknown")
            }
            variant={
              data.swappedInPeriod
                ? "error"
                : data.swappedInPeriod === false
                  ? "success"
                  : "neutral"
            }
          />
          <p className="text-sm text-text-secondary">
            {data.swappedPeriod
              ? t("sim-swap.window", { window: data.swappedPeriod })
              : t("sim-swap.window-unknown")}
          </p>
          <div className="w-full">
            {data.lastSimSwapDate && (
              <Field
                label={t("field.last-swap")}
                value={new Date(data.lastSimSwapDate).toLocaleDateString()}
              />
            )}
            <Field
              label={t("field.carrier")}
              value={data.carrierName ?? unknown}
            />
          </div>
        </div>
      );
    }

    case "identity_match": {
      const data = result.data as IdentityMatch;
      const fields = [
        ["first-name", data.firstNameMatch],
        ["last-name", data.lastNameMatch],
        ["address", data.addressLinesMatch],
        ["city", data.cityMatch],
        ["state", data.stateMatch],
        ["postal-code", data.postalCodeMatch],
        ["country", data.addressCountryMatch],
        ["national-id", data.nationalIdMatch],
        ["date-of-birth", data.dateOfBirthMatch],
      ] as const;

      return (
        <div className="flex flex-col gap-3">
          {/* Twilio's own roll-up. Never recomputed here: its published truth
              table contradicts itself, and the score is what it sells. */}
          {data.summaryScore !== null && (
            <div className="flex items-center gap-3">
              <span className="text-2xl font-semibold text-text-primary">
                {data.summaryScore}
              </span>
              <span className="text-sm text-text-secondary">
                {t("identity.summary-score")}
              </span>
            </div>
          )}
          <div>
            {fields
              .filter(([, level]) => level !== null)
              .map(([key, level]) => (
                <Field
                  key={key}
                  label={t(`identity.field.${key}`)}
                  value={t(`match-level.${level}`)}
                />
              ))}
          </div>
        </div>
      );
    }

    case "reassigned_number": {
      const data = result.data as ReassignedNumber;
      const answer = data.isNumberReassigned ?? "no_data_available";
      return (
        <div className="flex flex-col items-start gap-2">
          <StatusBadge
            label={t(`reassigned.${answer}`)}
            variant={
              answer === "yes"
                ? "error"
                : answer === "no"
                  ? "success"
                  : "neutral"
            }
          />
          <p className="text-sm text-text-secondary">
            {t(`reassigned-description.${answer}`)}
          </p>
        </div>
      );
    }

    case "sms_pumping_risk": {
      const data = result.data as SmsPumpingRisk;
      const score = data.smsPumpingRiskScore;
      // Twilio's own bands: 0-60 low, 60-75 mild, 75-90 moderate, 90-100 high.
      const band =
        score === null
          ? "unknown"
          : score >= 90
            ? "high"
            : score >= 75
              ? "moderate"
              : score >= 60
                ? "mild"
                : "low";

      // Twilio sends two independent signals: a score for the number and a
      // risk category for the carrier behind it. A real capture had score 43
      // ("low") on a carrier rated "high" — reading only the score told the
      // customer it was safe to send. The score stays the headline, because
      // Twilio's bands are what its own guidance is written against, but the
      // carrier's rating is shown next to it, and a carrier rated `high`
      // replaces the reassurance rather than sitting quietly underneath it.
      const carrierRisk = data.carrierRiskCategory;
      const carrierIsRisky = carrierRisk === "high";

      return (
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-semibold text-text-primary">
              {score ?? "—"}
            </span>
            <StatusBadge
              label={t(`risk.${band}`)}
              variant={
                band === "high"
                  ? "error"
                  : band === "moderate"
                    ? "warning"
                    : band === "low"
                      ? "success"
                      : "neutral"
              }
            />
          </div>
          <p className="text-sm text-text-secondary">
            {carrierIsRisky && band !== "high"
              ? t("risk.carrier-caveat")
              : t(`risk-description.${band}`)}
          </p>
          {carrierRisk && (
            <div className="w-full">
              <Field
                label={t("field.carrier-risk")}
                value={t(`risk.${carrierRisk}`)}
              />
            </div>
          )}
          {data.numberBlocked && (
            <p className="flex items-center gap-1 text-sm text-text-secondary">
              <Icon name="alert" size={16} />
              {t("risk.blocked")}
            </p>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}
